// materialMappingFromGlb — derive a material_mapping array from a rig GLB's
// glTF structure, client-side.
//
// WHY this exists: the avatar-asset-manager stores an uploaded rig but does NOT
// parse it, so `material_mapping` stays empty and the RigInspector shows "no
// material slots". This builds the same slot list the runtime expects and the
// caller PUTs it to /material-mapping.
//
// WHY node name (not mesh name): the runtime binds materials in
// `applyMaterialMapping` by THREE node name (`node.name`), which GLTFLoader
// derives from the glTF NODE name. Blender exports meaningful node names
// (e.g. "head_lod0_mesh") but junk mesh names (e.g. "Mesh.001"), so keying on
// mesh name would display slots that never bind. We key on node name.
//
// Pure: parses the GLB JSON chunk directly (no THREE / DRACO needed), so it's
// headless-testable and works on any .glb regardless of compression — Draco
// only affects geometry buffers, not the node/mesh JSON structure.

const MAGIC_GLTF = 0x46546c67 // 'glTF'
const CHUNK_JSON = 0x4e4f534a // 'JSON'

function toDataView(data) {
  if (data instanceof ArrayBuffer) return new DataView(data)
  if (ArrayBuffer.isView(data)) return new DataView(data.buffer, data.byteOffset, data.byteLength)
  throw new Error('buildMaterialMappingFromGlb: expected ArrayBuffer or typed array')
}

// Read the glTF JSON object out of a binary GLB (the first chunk).
function readGlbJson(data) {
  const dv = toDataView(data)
  if (dv.byteLength < 20) throw new Error('not a GLB: too small')
  if (dv.getUint32(0, true) !== MAGIC_GLTF) throw new Error('not a GLB: bad magic')
  const chunkLen = dv.getUint32(12, true)
  const chunkType = dv.getUint32(16, true)
  if (chunkType !== CHUNK_JSON) throw new Error('not a GLB: first chunk is not JSON')
  const start = dv.byteOffset + 20
  const bytes = new Uint8Array(dv.buffer, start, chunkLen)
  return JSON.parse(new TextDecoder().decode(bytes))
}

// Build [{ meshIndex, meshName, primitiveIndex }] — one entry per rendered mesh
// primitive, in scene-node order. `meshIndex` is a flat 0-based counter (a
// stable key for the mapping/UI); `meshName` is the node name the runtime binds
// against; `primitiveIndex` distinguishes multi-primitive meshes.
export function buildMaterialMappingFromGlb(data) {
  const gltf = readGlbJson(data)
  const nodes = Array.isArray(gltf.nodes) ? gltf.nodes : []
  const meshes = Array.isArray(gltf.meshes) ? gltf.meshes : []
  const mapping = []
  let flat = 0
  for (const node of nodes) {
    if (node == null || node.mesh == null) continue
    const mesh = meshes[node.mesh]
    if (!mesh || !Array.isArray(mesh.primitives) || mesh.primitives.length === 0) continue
    const name = node.name || mesh.name || `mesh_${node.mesh}`
    for (let pi = 0; pi < mesh.primitives.length; pi++) {
      mapping.push({ meshIndex: flat++, meshName: name, primitiveIndex: pi })
    }
  }
  return mapping
}
